import hoverBoxStyles from "../../../styles/hover.module.css";
import { useAppSelector } from "../../../store/hooks";
import { OperationalTask, WorkTask } from "../../../types/ganttChart";
import HoverBoxTitle from "./HoverBoxTitle";
import HoverTimestamp from "./HoverTimestamp";
import labels from "../../../utils/labels";
import VerticalGanttChartTransportationIcon from "../verticalGanttChart/VerticalGanttChartTransportationIcon";
import HoverDependencyItem from "./HoverDependencyArray";
import { getTransportationViolationMessagesFromViolationName } from "../../../utils/violationUtils";
import ViolationChip from "../../UI/chips/ViolationChip";
import useHoverBoxPosition from "../../../hooks/useHoverBoxPosition";
import { transportTypeAt } from "../../../utils/transportSegmentUtil";

const HoverBox = () => {
  const { taskIsHovered } = useAppSelector((state) => state.overlaySlice);
  const { xPosition, yPosition, borderRadius } = useHoverBoxPosition(
    taskIsHovered
      ? `${taskIsHovered.type}-${taskIsHovered.task.idTask}`
      : undefined,
    "vertical-ganttChart-svg",
    "hoverBox",
  );

  const getTitle = () => {
    switch (taskIsHovered?.type) {
      case "WORKTASK":
        const workTask = taskIsHovered.task as WorkTask;
        return workTask.client.description
          ? workTask.client.description
          : workTask.description;
      case "TRANSPORTATION_TASK":
        return "Transport";
      case "OPERATIONAL":
        const operationalTask = taskIsHovered.task as OperationalTask;
        return operationalTask.description;
      default:
        return "";
    }
  };

  const getIcon = () => {
    if (taskIsHovered?.type === "TRANSPORTATION_TASK") {
      return (
        <VerticalGanttChartTransportationIcon
          color="white"
          type={transportTypeAt(
            taskIsHovered.employee!.transportationType,
            taskIsHovered.task.start,
          )}
        />
      );
    }
  };

  const getWorkTask = () => {
    return taskIsHovered?.task as WorkTask;
  };

  const getOperationalTask = () => {
    return taskIsHovered?.task as OperationalTask;
  };

  // Empty when the task has no address (e.g. an event without a location), so the line is hidden.
  const getAddress = () => {
    const task = taskIsHovered?.task as WorkTask;
    const postalAndDistrict = [task.postalCode, task.district]
      .filter(Boolean)
      .join(" ");
    return [task.address, postalAndDistrict].filter(Boolean).join(", ");
  };

  return (
    <>
      {xPosition !== undefined && yPosition !== undefined && taskIsHovered && (
        <>
          <div
            className={hoverBoxStyles.hoverBox}
            id="hoverBox"
            style={{
              position: "absolute",
              left: xPosition,
              top: yPosition,
              borderRadius,
              zIndex: 2000,
            }}
          >
              <div className={hoverBoxStyles.header}>
                <HoverBoxTitle title={getTitle()} icon={getIcon()} />
                <HoverTimestamp
                  start={taskIsHovered.task.start}
                  end={taskIsHovered.task.end}
                  duration={taskIsHovered.task.duration}
                  type={taskIsHovered.type}
                />
              </div>
              <div className={hoverBoxStyles.body}>
                <p className="flex gap-[10px]">
                  {labels.hoverEmployee}
                  <span className="font-bold">
                    {taskIsHovered.employee?.fullName}
                  </span>
                </p>
                {(taskIsHovered.type === "WORKTASK" ||
                  taskIsHovered.type === "OPERATIONAL") &&
                  getAddress() && (
                    <p className="flex gap-[10px]">
                      {labels.address}
                      <span className="font-bold">{getAddress()}</span>
                    </p>
                  )}
                {taskIsHovered.type === "OPERATIONAL" && (
                  <p className="flex flex-col">
                    {labels.details}
                    <span className="font-bold">
                      {getOperationalTask().description}
                    </span>
                  </p>
                )}
                {taskIsHovered.type === "WORKTASK" && (
                  <>
                    {getWorkTask().subTasks?.length > 0 && (
                      <ul>
                        <li key="ydelser">{labels.hoverTasks}</li>
                        {getWorkTask().subTasks.map((subtask, index) => (
                          <li key={`tasks_${index}`} className="font-bold">
                            {subtask.description}
                          </li>
                        ))}
                      </ul>
                    )}
                    {getWorkTask().taskDependencies?.length > 0 && (
                      <ul>
                        <li>{labels.dependencies}</li>
                        {getWorkTask().taskDependencies.map(
                          (dependency, index) => (
                            <HoverDependencyItem
                              dependency={dependency}
                              key={`dependency_${index}`}
                            />
                          ),
                        )}
                      </ul>
                    )}
                  </>
                )}
                {taskIsHovered.type === "TRANSPORTATION_TASK" &&
                  "violations" in taskIsHovered.task &&
                  taskIsHovered.task.violations.length > 0 && (
                    <>
                      <ul className={hoverBoxStyles.hoverBoxList}>
                        {taskIsHovered.task.violations.map(
                          (violation, index) => (
                            <li
                              key={`violation_${index}`}
                              className={hoverBoxStyles.hoverBoxViolationText}
                            >
                              <ViolationChip />
                              {getTransportationViolationMessagesFromViolationName(
                                violation,
                              )}
                            </li>
                          ),
                        )}
                      </ul>
                    </>
                  )}
              </div>
          </div>
        </>
      )}
    </>
  );
};

export default HoverBox;
